import { Component } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * Related products from Shopify's recommendations API.
 *
 * Fetched once, as the section comes near the viewport, so it never competes
 * with the product's own images while the page is still painting. The response
 * is a render of this same section, so the cards come from
 * `snippets/product-card.liquid` exactly as they do everywhere else.
 *
 * Deferring is an optimisation, never the only route to loading: see the
 * idle fallback in connectedCallback.
 *
 * @extends {Component}
 */
class ProductRecommendationsComponent extends Component {
  #loaded = false;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    const url = this.dataset.url;
    if (!url) return;

    // Fast path: load as the section comes near the viewport.
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#load(url);
      },
      { rootMargin: '400px' }
    );
    this.#observer.observe(this);

    // Guarantee. An IntersectionObserver never reports an element with no
    // usable box — the callback does not fire at all, not even as "not
    // intersecting" — and this section renders empty until it loads, so it
    // can legitimately be in that state. Deferring must therefore never be
    // the only path to loading. Once the page is loaded and idle, fetch
    // regardless: by then nothing is competing with it anyway.
    this.#whenIdle(() => this.#load(url));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @param {() => void} callback */
  #whenIdle(callback) {
    const idle = () =>
      'requestIdleCallback' in window
        ? window.requestIdleCallback(callback, { timeout: 3000 })
        : setTimeout(callback, 1000);

    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  /** @param {string} url */
  async #load(url) {
    // Called from both the observer and the idle fallback, whichever wins.
    if (this.#loaded) return;
    this.#loaded = true;
    this.#observer?.disconnect();

    try {
      const response = await fetch(url);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = parsed.querySelector('product-recommendations-component');

      if (incoming) morph(this, incoming, { childrenOnly: true });
    } catch (error) {
      // No recommendations is a perfectly good outcome; the section stays
      // empty and hides itself.
      console.warn('[product-recommendations]', error);
    }
  }
}

if (!customElements.get('product-recommendations-component')) {
  customElements.define('product-recommendations-component', ProductRecommendationsComponent);
}
